import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { tasksApi, taskTypesApi } from "../../api/endpoints";
import { TaskCard, isOverdue } from "./TaskCard";
import type { Task, TaskType } from "../../types/domain";

interface Props {
  onOpen: (task: Task) => void;
  /** Опциональный пред-фильтр по запуску (см. TasksPage). */
  launchFilter?: (t: Task) => boolean;
}

interface Group {
  id: string;
  title: string;
  hint: string;
  match: (t: Task) => boolean;
  tone: "danger" | "warn" | "muted";
}

const GROUPS: Group[] = [
  {
    id: "overdue",
    title: "Просрочено",
    hint: "Дедлайн прошёл, задача не закрыта",
    match: (t) => isOverdue(t),
    tone: "danger",
  },
  {
    id: "critical",
    title: "Критично",
    hint: "Задача отмечена критическим риском",
    match: (t) => t.riskLevel === "critical" && t.status !== "done" && t.status !== "cancelled",
    tone: "danger",
  },
  {
    id: "risk",
    title: "Риск",
    hint: "Помечено как риск, требует внимания",
    match: (t) => t.riskLevel === "risk" && t.status !== "done" && t.status !== "cancelled",
    tone: "warn",
  },
  {
    id: "no-date",
    title: "Без срока",
    hint: "Нет ни даты выполнения, ни дедлайна",
    match: (t) =>
      !t.plannedDate &&
      !t.dueDate &&
      t.status !== "done" &&
      t.status !== "cancelled",
    tone: "muted",
  },
  {
    id: "no-assignee",
    title: "Без ответственного",
    hint: "Нет назначенного исполнителя",
    match: (t) =>
      !t.assigneeName &&
      t.status !== "done" &&
      t.status !== "cancelled",
    tone: "muted",
  },
];

export function TasksRisksView({ onOpen, launchFilter }: Props) {
  const tasksQuery = useQuery({
    queryKey: ["tasks", "all"],
    queryFn: async () => (await tasksApi.list({ includeUndated: true })).tasks,
    staleTime: 5_000,
  });

  const taskTypesQuery = useQuery({
    queryKey: ["taskTypes"],
    queryFn: async () => (await taskTypesApi.list()).types,
    staleTime: 60_000,
  });
  const taskTypes = taskTypesQuery.data ?? [];
  const typeById = useMemo(
    () => new Map<string, TaskType>(taskTypes.map((t) => [t.id, t])),
    [taskTypes],
  );

  const groups = useMemo(() => {
    const all = (tasksQuery.data ?? []).filter((t) =>
      launchFilter ? launchFilter(t) : true,
    );
    return GROUPS.map((g) => ({
      ...g,
      tasks: all.filter(g.match).sort(byPriority),
    }));
  }, [tasksQuery.data, launchFilter]);

  const totalAffected = useMemo(() => {
    const all = (tasksQuery.data ?? []).filter((t) =>
      launchFilter ? launchFilter(t) : true,
    );
    return all.filter((t) => GROUPS.some((g) => g.match(t))).length;
  }, [tasksQuery.data, launchFilter]);

  if (tasksQuery.isLoading) {
    return <div className="tasks-stub">Загружаем…</div>;
  }
  if (tasksQuery.isError) {
    return (
      <div className="tasks-stub" style={{ color: "#DC2626" }}>
        Не удалось загрузить задачи. Перезагрузите страницу.
      </div>
    );
  }

  if (totalAffected === 0) {
    return (
      <div className="tasks-stub tasks-risks-empty">
        <h3>Всё под контролем</h3>
        <p>Нет просроченных, рискованных или потерянных задач. Так держать.</p>
      </div>
    );
  }

  return (
    <div className="tasks-risks">
      {groups
        .filter((g) => g.tasks.length > 0)
        .map((g) => (
          <section key={g.id} className={"tasks-risk-group tone-" + g.tone}>
            <header className="tasks-risk-head">
              <h3>
                {g.title}
                <span className="tasks-risk-count">{g.tasks.length}</span>
              </h3>
              <span className="tasks-risk-hint">{g.hint}</span>
            </header>
            <div className="tasks-risk-list">
              {g.tasks.map((t) => (
                <TaskCard
                  key={g.id + ":" + t.id}
                  task={t}
                  type={typeById.get(t.taskTypeId) ?? null}
                  onOpen={() => onOpen(t)}
                  onDragStart={() => {}}
                  onDragEnd={() => {}}
                  isDragging={false}
                  draggable={false}
                />
              ))}
            </div>
          </section>
        ))}
    </div>
  );
}

/** Порядок задач внутри группы: критичные/просроченные сначала,
 *  потом по дедлайну (ближний раньше), потом по plannedDate, потом по createdAt. */
function byPriority(a: Task, b: Task): number {
  const aOverdue = isOverdue(a) ? 0 : 1;
  const bOverdue = isOverdue(b) ? 0 : 1;
  if (aOverdue !== bOverdue) return aOverdue - bOverdue;

  const riskRank = (t: Task) =>
    t.riskLevel === "critical" ? 0 : t.riskLevel === "risk" ? 1 : 2;
  if (riskRank(a) !== riskRank(b)) return riskRank(a) - riskRank(b);

  if (a.dueDate && b.dueDate) {
    if (a.dueDate !== b.dueDate) return a.dueDate.localeCompare(b.dueDate);
  } else if (a.dueDate) return -1;
  else if (b.dueDate) return 1;

  if (a.plannedDate && b.plannedDate) {
    if (a.plannedDate !== b.plannedDate) return a.plannedDate.localeCompare(b.plannedDate);
  } else if (a.plannedDate) return -1;
  else if (b.plannedDate) return 1;

  return a.createdAt.localeCompare(b.createdAt);
}
